// components/InternalAudit.jsx
import { useContext, useEffect, useState } from "react"
import { AppContext } from "../App"
import { getInlineLoader } from "../Helpers";
import Footer from "../ones/Footer";
import HorizontalImagesStrip from "../ones/HorizontalImagesStrip";

import banner1 from '../assets/banner1.jpg';
import banner2 from '../assets/banner2.jpg';
import banner3 from '../assets/banner3.jpg';
import banner4 from '../assets/banner4.jpg';

import {
  IoMdCheckmarkCircleOutline,
  IoMdSearch,
  IoMdBuild,
  IoMdArrowDropright,
  IoMdPeople,
  IoMdDocument,
  IoMdAlert,
  IoMdTrendingUp,
  IoMdLock,
  IoMdGlobe,
  IoMdCall
} from "react-icons/io";
import { FaBalanceScale, FaShieldAlt, FaLaptopCode, FaHandHoldingUsd, FaSearchDollar, FaGavel } from "react-icons/fa";
import { FaShield } from "react-icons/fa6";

export default function InternalAudit(props) {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);
  const [bgIndex, setBgIndex] = useState(0);
  
  const banners = [banner1, banner2, banner3, banner4];

  const servicePillars = [
    {
      id: 1,
      title: "Statutory & Internal Audit",
      icon: <FaShieldAlt />,
      description: "High-level assurance through financial statement audits (ISAs) and internal control evaluations to improve governance.",
      features: [
        "Statutory Audit (Financial Statements)",
        "Risk Management & Governance",
        "Internal Controls Design & Review"
      ],
      accent: "#313131",
      bgLight: "#fff7ed"
    },
    {
      id: 2,
      title: "Forensic & ICT Audits",
      icon: <FaSearchDollar />,
      description: "Specialized investigations and assessments of IT systems for security, integrity, and fraud prevention.",
      features: [
        "Forensic Audits & Investigations",
        "ICT Security & Integrity",
        "Fraud Detection Systems"
      ],
      accent: "#dc2626",
      bgLight: "#fef2f2"
    },
    {
      id: 3,
      title: "Project & Grant Assurance",
      icon: <FaHandHoldingUsd />,
      description: "Audit services for donor-funded projects and grants, ensuring compliance with funding requirements.",
      features: [
        "Donor-Funded Project Audits",
        "Grant Compliance Reporting",
        "Agreed-Upon Procedures (AUP)"
      ],
      accent: "#059669",
      bgLight: "#f0fdf4"
    },
    {
      id: 4,
      title: "Due Diligence & Value Audits",
      icon: <FaBalanceScale />,
      description: "Analyzing financial health and procurement transparency for mergers, acquisitions, and investments.",
      features: [
        "M&A Due Diligence",
        "Value for Money Audits",
        "Operational Stability Analysis"
      ],
      accent: "#2563eb",
      bgLight: "#eff6ff"
    }
  ];

  const focusAreas = [
    { name: "Statutory (ISAs)", icon: <IoMdDocument /> },
    { name: "ICT & Security", icon: <FaLaptopCode /> },
    { name: "Forensic/Fraud", icon: <IoMdSearch /> },
    { name: "Procurement", icon: <IoMdTrendingUp /> },
    { name: "Donor/Grants", icon: <IoMdGlobe /> },
    { name: "Legal Compliance", icon: <FaGavel /> },
    { name: "Risk Management", icon: <IoMdAlert /> },
    { name: "Mergers (M&A)", icon: <IoMdPeople /> }
  ];

  async function init() {
    setReady(false);
    setTimeout(() => setReady(true), 300);
  }

  useEffect(() => { init(); }, []);

  useEffect(() => {
    if (!ready) return;
    const interval = setInterval(() => {
      setBgIndex(prev => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [ready]);

  if (!ready) return (
    <div className="container d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
      {getInlineLoader()}
    </div>
  );

  return (
    <div className="bg-white">
      {/* ================= HERO SECTION (CLEANER LIGHT VERSION) ================= */}
      <section className="position-relative overflow-hidden" style={{ height: "85vh", minHeight: "600px" }}>
        {banners.map((banner, i) => (
          <div
            key={i}
            style={{
              backgroundImage: `url(${banner})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              width: "100%",
              height: "100%",
              position: "absolute",
              top: 0,
              left: 0,
              transition: "opacity 1.5s ease-in-out",
              opacity: i === bgIndex ? 0.15 : 0, // Very subtle background
              zIndex: 0
            }}
          />
        ))}

        <div className="container h-100 position-relative z-1 d-flex align-items-center">
          <div className="row">
            <div className="col-lg-7">
              <span className="badge mb-3 px-3 py-2 text-uppercase fw-bold" 
                    style={{ background: "#c7ccfe", color: "#444444", letterSpacing: "1px", fontSize: "12px" }}>
                Audit & Assurance Excellence
              </span>
              <h1 className="display-3 fw-bold text-dark mb-4" style={{ lineHeight: "1.1" }}>
                Precision in <span style={{ color: "#3244e2" }}>Assurance.</span><br />
                Trust in Results.
              </h1>
              <p className="lead text-muted font-semi-bold mb-5 shadow-sm p-3 bg-white border-start border-4 border-primary rounded">
                ATRIC provides independent, risk-based auditing to protect organizational value and ensure commercial viability across East Africa.
              </p>
              <div className="d-flex gap-3">
                <button 
                  className="btn btn-dark btn-lg px-4 rounded-0 shadow"
                  onClick={() => appContext.navTo({ item: 'contact_us', service: 'Audit' })}
                >
                  Consult an Auditor
                </button>
                <a href="#services" className="btn btn-outline-dark btn-lg px-4 rounded-0">Our Catalog</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= SERVICE PILLARS (CLEAN CARDS) ================= */}
      <section className="py-5" id="services" style={{ background: "#f8fafc" }}>
        <div className="container py-5">
          <div className="text-center mb-5">
            <h2 className="fw-bold text-dark">Audit Specializations</h2>
            <div className="mx-auto mt-2" style={{ width: "60px", height: "4px", background: "#57668d" }}></div>
          </div>

          <div className="row g-4">
            {servicePillars.map((pillar) => (
              <div className="col-md-6 col-lg-3" key={pillar.id}>
                <div 
                  className="card h-100 border-0 shadow-sm transition-up"
                  style={{ 
                    transition: "transform 0.3s ease", 
                    borderRadius: "12px",
                    overflow: "hidden"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-10px)"}
                  onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
                >
                  <div className="p-4">
                    <div className="mb-4 d-inline-flex p-3 rounded-circle" 
                         style={{ backgroundColor: pillar.bgLight, color: pillar.accent, fontSize: "1.5rem" }}>
                      {pillar.icon}
                    </div>
                    <h5 className="fw-bold mb-3">{pillar.title}</h5>
                    <p className="text-muted small mb-4">{pillar.description}</p>
                    <ul className="list-unstyled mb-0">
                      {pillar.features.map((f, i) => (
                        <li key={i} className="d-flex align-items-start gap-2 mb-2 small text-dark fw-medium">
                          <IoMdCheckmarkCircleOutline className="mt-1" style={{ color: pillar.accent }} />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-4 pt-0 mt-auto">
                    <hr className="opacity-10" />
                    <button 
                       className="btn btn-link p-0 text-decoration-none fw-bold small text-dark d-flex align-items-center gap-1"
                       onClick={() => appContext.navTo({ item: 'contact_us', service: pillar.title })}
                    >
                      LEARN MORE <IoMdArrowDropright />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FOCUS AREAS (MINIMALIST GRID) ================= */}
      <section className="py-5 bg-white">
        <div className="container py-4">
          <div className="row align-items-center">
            <div className="col-lg-4 mb-4 mb-lg-0">
              <h3 className="fw-bold">Broad-Spectrum <br/>Audit Focus</h3>
              <p className="text-muted">From statutory mandates to complex forensic investigations, we cover the full range of assurance needs.</p>
            </div>
            <div className="col-lg-8">
              <div className="row g-2">
                {focusAreas.map((area, idx) => (
                  <div className="col-6 col-md-3" key={idx}>
                    <div className="p-3 border rounded text-center bg-light-subtle">
                      <div className="text-primary mb-1" style={{ fontSize: "1.2rem" }}>{area.icon}</div>
                      <div className="small fw-bold">{area.name}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= CTA SECTION (HIGH CONTRAST) ================= */}
      <section className="py-5 bg-dark text-white text-center">
        <div className="container py-4">
          <h2 className="fw-bold mb-4">Integrity in Every Ledger.</h2>
          <p className="opacity-75 mb-5 mx-auto" style={{ maxWidth: "600px" }}>
            Ready to secure your organizational future? Partner with ATRIC for independent, high-level assurance.
          </p>
          <div className="d-flex justify-content-center flex-wrap gap-3">
            <button className="btn btn-primary btn-lg px-5 fw-bold" onClick={() => appContext.navTo({ item: 'contact_us' })}>
              Schedule Consultation
            </button>
            <a href="tel:+255717918808" className="btn btn-outline-light btn-lg px-4 d-flex align-items-center gap-2">
              <IoMdCall /> Call Expert
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}